import React, { useEffect, useState, useCallback } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { platformApiFetch } from './platformApi';
import { usePlatformAuth } from '../context/PlatformAuthContext';

const STATUSES = ['active', 'paused', 'disabled'];

function parseEventTypes(text) {
  const parts = text
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.length ? parts : ['*'];
}

export default function PlatformWebhookFormPage() {
  const { webhookId } = useParams();
  const isEdit = Boolean(webhookId);
  const navigate = useNavigate();
  const { user, ready } = usePlatformAuth();

  const [tenantId, setTenantId] = useState('');
  const [name, setName] = useState('');
  const [targetUrl, setTargetUrl] = useState('');
  const [eventsText, setEventsText] = useState('*');
  const [maxAttempts, setMaxAttempts] = useState('5');
  const [delaySec, setDelaySec] = useState('60');
  const [status, setStatus] = useState('active');
  const [regenerateSecret, setRegenerateSecret] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [secretDialog, setSecretDialog] = useState(null);

  const load = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    setError(null);
    try {
      const res = await platformApiFetch(`/api/platform/webhooks/${webhookId}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load');
      const w = json.webhook;
      setTenantId(String(w.tenant_id));
      setName(w.webhook_name || '');
      setTargetUrl(w.target_url || '');
      setEventsText((w.event_types || ['*']).join(', '));
      setMaxAttempts(String(w.retry_policy?.max_attempts ?? 5));
      setDelaySec(String(w.retry_policy?.initial_delay_seconds ?? 60));
      setStatus(w.status || 'active');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [isEdit, webhookId]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const body = {
      webhook_name: name.trim(),
      target_url: targetUrl.trim(),
      event_types: parseEventTypes(eventsText),
      retry_policy: {
        max_attempts: Number(maxAttempts) || 5,
        initial_delay_seconds: Number(delaySec) || 60
      },
      status
    };
    if (!isEdit) body.tenant_id = Number(tenantId);
    if (isEdit && regenerateSecret) body.regenerate_secret = true;
    try {
      const url = isEdit ? `/api/platform/webhooks/${webhookId}` : '/api/platform/webhooks';
      const res = await platformApiFetch(url, { method: isEdit ? 'PATCH' : 'POST', body });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Save failed');
      if (json.secretShownOnce) {
        setSecretDialog({ secret: json.secretShownOnce, id: json.webhook?.id });
      } else {
        navigate(`/platform/webhooks/${json.webhook.id}`, { replace: true });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const copySecret = async () => {
    if (!secretDialog?.secret) return;
    try {
      await navigator.clipboard.writeText(secretDialog.secret);
    } catch (_e) {
      /* ignore */
    }
  };

  const closeSecret = () => {
    const id = secretDialog?.id;
    setSecretDialog(null);
    if (id) navigate(`/platform/webhooks/${id}`, { replace: true });
  };

  if (!ready) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (user?.role !== 'super_admin') {
    return <Navigate to="/platform/analytics" replace />;
  }

  if (isEdit && loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isEdit ? 'Edit webhook (platform)' : 'New webhook (platform)'}
      </Typography>
      <Button component={Link} to={isEdit ? `/platform/webhooks/${webhookId}` : '/platform/webhooks'} sx={{ mb: 2 }}>
        Back
      </Button>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, maxWidth: 720 }}>
        <Box component="form" onSubmit={submit}>
          {!isEdit && (
            <TextField
              fullWidth
              required
              label="Tenant ID"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              margin="normal"
              helperText="Numeric tenant primary key"
            />
          )}
          <TextField
            fullWidth
            required
            label="Webhook name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            margin="normal"
            inputProps={{ maxLength: 255 }}
          />
          <TextField
            fullWidth
            required
            label="Target URL"
            value={targetUrl}
            onChange={(e) => setTargetUrl(e.target.value)}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Event types"
            value={eventsText}
            onChange={(e) => setEventsText(e.target.value)}
            margin="normal"
            multiline
            minRows={2}
          />
          <TextField
            fullWidth
            label="Retry max attempts"
            type="number"
            value={maxAttempts}
            onChange={(e) => setMaxAttempts(e.target.value)}
            margin="normal"
            inputProps={{ min: 0, max: 20 }}
          />
          <TextField
            fullWidth
            label="Retry initial delay (seconds)"
            type="number"
            value={delaySec}
            onChange={(e) => setDelaySec(e.target.value)}
            margin="normal"
            inputProps={{ min: 1, max: 3600 }}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {isEdit && (
            <FormControlLabel
              control={
                <Checkbox checked={regenerateSecret} onChange={(e) => setRegenerateSecret(e.target.checked)} />
              }
              label="Regenerate signing secret"
              sx={{ mt: 1, display: 'block' }}
            />
          )}

          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={saving || !name.trim() || !targetUrl.trim() || (!isEdit && !tenantId.trim())}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button component={Link} to={isEdit ? `/platform/webhooks/${webhookId}` : '/platform/webhooks'}>
              Cancel
            </Button>
          </Box>
        </Box>
      </Paper>

      <Dialog open={Boolean(secretDialog)} onClose={closeSecret}>
        <DialogTitle>Signing secret (shown once)</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>Copy now; it cannot be retrieved later.</DialogContentText>
          <TextField
            fullWidth
            multiline
            minRows={2}
            value={secretDialog?.secret || ''}
            InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
          />
        </DialogContent>
        <DialogActions>
          <Button startIcon={<ContentCopyIcon />} onClick={copySecret}>
            Copy
          </Button>
          <Button variant="contained" onClick={closeSecret}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
